import { FC, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { List, Collapse } from 'antd'
import UploadRevisionButton from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/ImDfmModal/SlideActionPanel/UploadRevisionButton'
import {
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory_qmrs_attachmentLink_quoteAttachmentRevision_attachment as AttachmentType,
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory_qmrs_dfmFeedback as DfmFeedbackType,
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory_qmrs_partRevision as PartRevisionType,
    qmrsGroupedByDfmCategory_qmrsGroupedByDfmCategory_qmrs as QmrType,
} from '@Builder/QuoteDetailPage/FilesTab/ImQuoteTabSections/ManufacturabilityFeedbackTab/__generated__/qmrsGroupedByDfmCategory'

import Box from '~/core/Box'
import Button from '~/core/Button'
import Link from '~/core/Link'
import { REQUESTED, DRAFT } from '~/lib/quoteStatuses'
import quoteStatusQuery from '../quoteStatusQuery'

import DfmTopicsTable from './DfmTopicsTable'
import PartName from './PartName'
import styles from './styles.css'

const PLACEMENT = 'im-dfm-part-row'
const QUOTE_STATE_DRAFT_MESSAGE =
    'Please request a quote to receive DFM feedback'
const QUOTE_STATE_REQUESTED_MESSAGE = 'Requested'
const DFM_NOT_AVAILABLE = 'No DFM feedback available'

interface PartRowPropsType {
    attachmentFileName?: AttachmentType['fileName']
    dfmFeedback: DfmFeedbackType[]
    fileName: QmrType['fileName']
    isImQuoteType?: boolean
    onClickViewDfmModal: (qmrId: string, dfmId?: string) => void
    qmrId: string
    thumbnailUri: PartRevisionType['thumbnailUri']
    version: QmrType['version']
    isReleaseDfmClicked?: boolean
    refetch?: () => void
    isQuoteLocked?: boolean | null | undefined
    isHighTouch: boolean
    isNonImDfmEnabled: boolean
}

const PartRow: FC<PartRowPropsType> = ({
    attachmentFileName,
    dfmFeedback,
    fileName,
    isImQuoteType,
    onClickViewDfmModal,
    qmrId,
    thumbnailUri,
    version,
    isReleaseDfmClicked,
    refetch,
    isQuoteLocked,
    isHighTouch,
    isNonImDfmEnabled,
}) => {
    const { quoteId } = useParams<{ quoteId: string }>()

    const [showDfmTopics, setShowDfmTopics] = useState<boolean>(false)
    const [activeKey, setActiveKey] = useState<[number]>([0])

    const { data } = useQuery(quoteStatusQuery, {
        variables: { id: quoteId },
        skip: !quoteId,
    })

    const dfmAvailabilty = isReleaseDfmClicked && dfmFeedback?.length === 0
    const quoteStatus = data?.quote?.status
    const partsWithDfmFeedback = dfmFeedback?.length !== 0

    const FictivReviewMsg = () => {
        if (quoteStatus === DRAFT && !dfmAvailabilty) {
            return QUOTE_STATE_DRAFT_MESSAGE
        }
        if (quoteStatus === REQUESTED) {
            return QUOTE_STATE_REQUESTED_MESSAGE
        }
        if (dfmAvailabilty) {
            return DFM_NOT_AVAILABLE
        }
    }

    const toggleDfmTopicsTable = (evt: Event) => {
        evt.preventDefault()
        setActiveKey(prevState => {
            const [key] = prevState

            if (key === 1) {
                return [0]
            }
            return [1]
        })
        setShowDfmTopics(!showDfmTopics)
    }

    return (
        <List.Item className={styles.partRow}>
            <Collapse bordered={false} activeKey={activeKey}>
                <Collapse.Panel
                    key={1}
                    header={
                        <Box
                            justifyContent="between"
                            padding="16px 0"
                            width="100%"
                            className={styles.rowHeader}
                        >
                            <Box
                                alignItems="start"
                                flex={4}
                                className={styles.partName}
                            >
                                <PartName
                                    attachmentFileName={attachmentFileName}
                                    fileName={fileName}
                                    thumbnailUri={thumbnailUri}
                                    version={version}
                                />
                            </Box>
                            {partsWithDfmFeedback ? (
                                <Box className={styles.partRowButtons}>
                                    <Box className={styles.dfmFeedbackButtons}>
                                        <Button
                                            data-test-target="part-view-feedback"
                                            name="view-feedback"
                                            onClick={() => {
                                                onClickViewDfmModal(qmrId)
                                            }}
                                            placement={PLACEMENT}
                                            style={{ marginRight: '8px' }}
                                            type="primary"
                                        >
                                            View feedback
                                        </Button>
                                        <UploadRevisionButton
                                            isImQuoteType={isImQuoteType}
                                            isLocatedInImDfmModal={false}
                                            placement={PLACEMENT}
                                            qmrId={qmrId}
                                            refetch={refetch}
                                            isQuoteLocked={isQuoteLocked}
                                        />
                                    </Box>
                                    <Box className={styles.showHideDfmTopics}>
                                        <Link
                                            data-test-target="expand-part-row-dfms"
                                            name="expand-part-row-dfms"
                                            onClick={toggleDfmTopicsTable}
                                            placement={PLACEMENT}
                                            style={{
                                                fontSize: '14px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {`${
                                                showDfmTopics ? 'Hide' : 'Show'
                                            } DFM ${
                                                !isImQuoteType &&
                                                isHighTouch &&
                                                isNonImDfmEnabled
                                                    ? 'titles'
                                                    : 'topics'
                                            }`}
                                        </Link>
                                    </Box>
                                </Box>
                            ) : (
                                <Box>{FictivReviewMsg()}</Box>
                            )}
                        </Box>
                    }
                    showArrow={false}
                >
                    <DfmTopicsTable
                        onClickTopic={(dfmId: string) => {
                            onClickViewDfmModal(qmrId, dfmId)
                        }}
                        qmrId={qmrId}
                        placement={PLACEMENT}
                        toggleDfmTopicsTable={toggleDfmTopicsTable}
                        isHighTouch={isHighTouch}
                        isImQuoteType={Boolean(isImQuoteType)}
                        isNonImDfmEnabled={isNonImDfmEnabled}
                    />
                </Collapse.Panel>
            </Collapse>
        </List.Item>
    )
}

export default PartRow
